import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Buildings } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { createOrganization } from '../lib/api';

export default function NewOrganizationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    org_number: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    subscription_plan: 'basic',
    max_users: 50,
    max_properties: 5
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Ange ett namn för föreningen');
      return;
    }
    
    setLoading(true);
    
    try {
      const { data } = await createOrganization(formData);
      toast.success('Föreningen har skapats');
      navigate(`/superadmin/organizations/${data.id}`);
    } catch (error) {
      toast.error('Kunde inte skapa föreningen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-2xl" data-testid="new-org-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/superadmin/organizations')}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          <ArrowLeft size={16} />
          Föreningar
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
            <Buildings size={20} className="text-slate-600" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Ny förening
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card-flat space-y-6">
          <h2 className="text-base font-medium text-slate-900">Grunduppgifter</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Föreningsnamn *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="BRF Solsidan"
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Organisationsnummer</Label>
              <Input
                value={formData.org_number}
                onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                placeholder="802000-1234"
                className="font-mono"
                data-testid="org-number-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Adress</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Storgatan 1, 111 22 Stockholm"
              data-testid="org-address-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kontakt-e-post</Label>
              <Input
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="styrelsen@brf-solsidan.se"
                data-testid="org-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Kontakttelefon</Label>
              <Input
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                placeholder="08-123 45 67"
                data-testid="org-phone-input"
              />
            </div>
          </div>
        </div>

        <div className="card-flat space-y-6">
          <h2 className="text-base font-medium text-slate-900">Abonnemang</h2>
          
          <div className="space-y-2">
            <Label>Plan</Label>
            <Select
              value={formData.subscription_plan}
              onValueChange={(value) => setFormData({ ...formData, subscription_plan: value })}
            >
              <SelectTrigger data-testid="plan-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic - Grundfunktioner</SelectItem>
                <SelectItem value="standard">Standard - Fler funktioner</SelectItem>
                <SelectItem value="premium">Premium - Alla funktioner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Max antal användare</Label>
              <Input
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) || 50 })}
                data-testid="max-users-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Max antal fastigheter</Label>
              <Input
                type="number"
                value={formData.max_properties}
                onChange={(e) => setFormData({ ...formData, max_properties: parseInt(e.target.value) || 5 })}
                data-testid="max-properties-input"
              />
            </div>
          </div>
          
          <p className="text-xs text-slate-500">
            Föreningen får automatiskt en 30-dagars trial-period.
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/superadmin/organizations')}
          >
            Avbryt
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="btn-press"
            data-testid="create-org-button"
          >
            {loading ? 'Skapar...' : 'Skapa förening'}
          </Button>
        </div>
      </form>
    </div>
  );
}
